import { Menu } from 'antd'
import type { FC } from 'react'
import { memo, useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { PATH_SEGMENT } from 'routes/pathSegments'

import { profileMenuConfig } from 'features/Profile/config'
import styles from './menuWidget.module.scss'
import type { MenuWidgetProps } from './menuWidget.types'

export const iconStyle = {
  color: 'var(--secondary-100)',
  fontSize: '16px',
}

const menuPaths = [
  PATH_SEGMENT.PERSONAL_PROFILE,
  PATH_SEGMENT.PREFERENCES,
  PATH_SEGMENT.CHANGE_PASSWORD,
  PATH_SEGMENT.DEPOSIT,
  PATH_SEGMENT.MAILBOX_PROFILE,
  PATH_SEGMENT.CREDIT_CARDS,
  PATH_SEGMENT.BILLING_HISTORY,
  PATH_SEGMENT.PAYMENTS_HISTORY,
  PATH_SEGMENT.COMPLETED_BOOKINGS,
  PATH_SEGMENT.MEDIA_PREFERENCES,
  PATH_SEGMENT.TRANSACTIONS_MAPPING,
  PATH_SEGMENT.TRANSACTIONS_HISTORY,
  PATH_SEGMENT.PRESCRIPTIONS,
  PATH_SEGMENT.TEST_RESULTS,
]

export const MenuWidget: FC<MenuWidgetProps> = memo(({ isStaff, hasEmrEnabled }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { siteId } = useParams<{ siteId: string }>()

  const navigableKeys = useMemo(
    () => profileMenuConfig.getNavigableKeys(isStaff, hasEmrEnabled),
    [isStaff, hasEmrEnabled],
  )

  const selectedKeys = useMemo(() => {
    let pathname = location.pathname.split('/').at(-1) || ''
    pathname = pathname.split('?')[0].replace(/\/$/, '')

    if (location.pathname.includes(`/${PATH_SEGMENT.PRESCRIPTIONS}/`)) {
      return [PATH_SEGMENT.PRESCRIPTIONS]
    }

    const selectedKey = menuPaths.find((segment) => pathname === segment)
    return selectedKey ? [selectedKey] : [PATH_SEGMENT.PERSONAL_PROFILE]
  }, [location.pathname])

  const openKeys = useMemo(() => {
    const accountsRoutes = [
      PATH_SEGMENT.PERSONAL_PROFILE,
      PATH_SEGMENT.PREFERENCES,
      PATH_SEGMENT.CHANGE_PASSWORD,
    ]
    const financeRoutes = [
      PATH_SEGMENT.BILLING_HISTORY,
      PATH_SEGMENT.PAYMENTS_HISTORY,
      PATH_SEGMENT.DEPOSIT,
    ]

    const patientDataRoutes = [PATH_SEGMENT.ALL_PRESCRIPTIONS, PATH_SEGMENT.DISTINCT_PATIENTS]

    if (
      location.pathname.includes(`/${PATH_SEGMENT.PRESCRIPTIONS}/`) ||
      patientDataRoutes.includes(selectedKeys[0])
    ) {
      return ['patient-data']
    }

    if (accountsRoutes.includes(selectedKeys[0])) {
      return ['account']
    }
    if (financeRoutes.includes(selectedKeys[0])) {
      return ['finance']
    }
    return []
  }, [selectedKeys, location.pathname])

  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      if (!navigableKeys.includes(key)) {
        return
      }

      navigate(`/${siteId}/${PATH_SEGMENT.PROFILE}/${key}`)
    },
    [navigate, siteId, navigableKeys],
  )

  return (
    <Menu
      className={styles.container}
      selectedKeys={selectedKeys}
      defaultOpenKeys={openKeys}
      mode='inline'
      items={profileMenuConfig.getMenus(isStaff, hasEmrEnabled)}
      onClick={handleMenuClick}
    />
  )
})
